import { useEffect, useRef } from 'react';

export type SuccessEvent = {
  code: string;
  state?: string;
  idpRedirectUri?: string;
};

type ErrorType = 'validation_error' | 'employer_connection_error';
export type ErrorEvent = {
  errorMessage: string;
  errorType?: ErrorType;
};

type ApiConfig = {
  connectUrl: string;
};

export type ConnectInitializeArgs = {
  onSuccess: (e: SuccessEvent) => void;
  onError: (e: ErrorEvent) => void;
  onClose: () => void;
  apiConfig?: ApiConfig;
};

export type ConnectLaunchArgs = {
  sessionId: string;
  state?: string;
  zIndex?: number;
};

export type ConnectPreviewLaunchArgs = {
  clientId: string;
  products: string[];
};

type OpenFn = (args: ConnectLaunchArgs) => void;

type OpenPreviewFn = (args: ConnectPreviewLaunchArgs) => void;

const POST_MESSAGE_NAME = 'finch-auth-message-v2' as const;

type FinchConnectAuthMessage = { name: typeof POST_MESSAGE_NAME } & (
  | {
      kind: 'closed';
    }
  | {
      kind: 'success';
      code: string;
      state?: string;
      idpRedirectUri?: string;
    }
  | {
      kind: 'error';
      error: { shouldClose: boolean; message: string; type: ErrorType };
    }
);

interface FinchConnectPostMessage {
  data: FinchConnectAuthMessage;
  origin: string;
}

const BASE_FINCH_CONNECT_URI = 'https://connect.tryfinch.com';

const FINCH_CONNECT_IFRAME_ID = 'finch-connect-iframe';

const appendBaseParams = (url: URL) => {
  url.searchParams.append('app_type', 'spa');
  // The host URL of the SDK. This is used to store the referrer for postMessage purposes
  url.searchParams.append('sdk_host_url', window.location.origin);
  url.searchParams.append('mode', 'employer');
  // replace with actual SDK version by rollup
  url.searchParams.append('sdk_version', 'react-SDK_VERSION');
};

export const constructAuthUrl = ({
  sessionId,
  state,
  apiConfig,
}: {
  sessionId: string;
  state?: string;
  apiConfig?: ApiConfig;
}) => {
  const CONNECT_URL = apiConfig?.connectUrl || BASE_FINCH_CONNECT_URI;

  const authUrl = new URL(`${CONNECT_URL}/authorize`);

  authUrl.searchParams.append('session', sessionId);
  if (state) authUrl.searchParams.append('state', state);

  appendBaseParams(authUrl);

  return authUrl.href;
};

export const constructPreviewUrl = ({
  clientId,
  products,
  apiConfig,
}: {
  clientId: string;
  products: string[];
  apiConfig?: ApiConfig;
}) => {
  const CONNECT_URL = apiConfig?.connectUrl || BASE_FINCH_CONNECT_URI;

  const previewUrl = new URL(`${CONNECT_URL}/authorize`);

  previewUrl.searchParams.append('preview', 'true');
  previewUrl.searchParams.append('client_id', clientId);
  previewUrl.searchParams.append('products', (products ?? []).join(' '));
  // This will be replaced by a universally allowed redirect URI in the backend
  // We won't ever redirect to anything in preview mode, so we just need a value to pass validation
  previewUrl.searchParams.append('redirect_uri', 'https://www.tryfinch.com');

  appendBaseParams(previewUrl);

  return previewUrl.href;
};

let isUseFinchConnectInitialized = false;

export const useFinchConnect = (
  initializeArgs: ConnectInitializeArgs
): { open: OpenFn; openPreview: OpenPreviewFn } => {
  const isHookMounted = useRef(false);

  useEffect(() => {
    if (!isHookMounted.current) {
      if (isUseFinchConnectInitialized) {
        console.error(
          'One useFinchConnect hook has already been registered. Please ensure to only call useFinchConnect once to avoid your event callbacks getting called more than once. You can pass in override options to the open function if you so require.'
        );
      } else {
        isUseFinchConnectInitialized = true;
      }

      isHookMounted.current = true;
    }
  }, []);

  const createAndAttachIFrame = ({
    src,
    zIndexOverride,
  }: {
    src: string;
    zIndexOverride?: number;
  }) => {
    if (!document.getElementById(FINCH_CONNECT_IFRAME_ID)) {
      const iframe = document.createElement('iframe');
      iframe.src = src;
      iframe.frameBorder = '0';
      iframe.id = FINCH_CONNECT_IFRAME_ID;
      iframe.style.position = 'fixed';
      iframe.style.zIndex = zIndexOverride?.toString() || '999';
      iframe.style.height = '100%';
      iframe.style.width = '100%';
      iframe.style.top = '0';
      iframe.style.backgroundColor = 'none transparent';
      iframe.style.border = 'none';
      iframe.allow = 'clipboard-write; clipboard-read';
      document.body.prepend(iframe);
      document.body.style.overflow = 'hidden';
    }
  };

  const open: OpenFn = (launchArgs) => {
    const iframeSrc = constructAuthUrl({
      sessionId: launchArgs.sessionId,
      state: launchArgs.state,
      apiConfig: initializeArgs.apiConfig,
    });

    createAndAttachIFrame({
      src: iframeSrc,
      zIndexOverride: launchArgs.zIndex,
    });
  };

  const openPreview: OpenPreviewFn = (launchArgs) => {
    const iframeSrc = constructPreviewUrl({
      clientId: launchArgs.clientId,
      products: launchArgs.products,
      apiConfig: initializeArgs.apiConfig,
    });

    createAndAttachIFrame({
      src: iframeSrc,
    });
  };

  const close = () => {
    const frameToRemove = document.getElementById(FINCH_CONNECT_IFRAME_ID);
    if (frameToRemove) {
      frameToRemove.parentNode?.removeChild(frameToRemove);
      document.body.style.overflow = 'inherit';
    }
  };

  useEffect(() => {
    function handleFinchAuth(event: FinchConnectPostMessage) {
      const CONNECT_URL = initializeArgs.apiConfig?.connectUrl || BASE_FINCH_CONNECT_URI;

      if (!event.data) return;
      if (event.data.name !== POST_MESSAGE_NAME) return;
      if (!event.origin.startsWith(CONNECT_URL)) return;

      if (event.data.kind !== 'error') close();

      switch (event.data.kind) {
        case 'closed':
          initializeArgs.onClose();
          break;
        case 'error':
          if (event.data.error?.shouldClose) close();

          initializeArgs.onError({
            errorMessage: event.data.error?.message,
            errorType: event.data.error?.type,
          });
          break;
        case 'success':
          initializeArgs.onSuccess({
            code: event.data.code,
            state: event.data.state,
            idpRedirectUri: event.data.idpRedirectUri,
          });
          break;
        default: {
          // This case should never happen, if it does it should be reported to us
          initializeArgs.onError({
            errorMessage: `Report to developers@tryfinch.com: unable to handle window.postMessage for:  ${JSON.stringify(
              event.data
            )}`,
          });
        }
      }
    }

    window.addEventListener('message', handleFinchAuth);
    return () => {
      window.removeEventListener('message', handleFinchAuth);
      isUseFinchConnectInitialized = false;
    };
  }, [initializeArgs.onClose, initializeArgs.onError, initializeArgs.onSuccess]);

  return {
    open,
    openPreview,
  };
};
